/**
 * Client-side progress for script generation. The API is a single blocking
 * request with no progress events, so these steps are paced on a timer to
 * describe what the model is doing, not to report it. The copy mirrors the
 * brief the model actually follows (StaticPrompt
 * "script_template_generation_system" in db/seeds.rb) — keep it true to that
 * prompt rather than promising work the system doesn't do. The last step
 * never completes on its own: it runs until the request actually resolves.
 */
import { useCallback, useEffect, useState } from "react";
import { generateMode } from "./utils";

// One step per skeleton group (see ScriptSkeleton): name, description,
// section headers, questions, grading notes, then the settle pass.
const initialSteps = [
  "Distilling what this role really needs",
  "Setting the hiring bar and the red flags to watch for",
  "Mapping the interview from background to problem solving",
  "Writing open-ended questions that draw out real stories",
  "Defining what a strong answer looks like for each question",
  "Making sure every question earns its place",
];

const regenerateSteps = [
  "Reading your script and the changes you asked for",
  "Working out which sections your changes touch",
  "Rewriting those sections and leaving the rest as they are",
  "Realigning the grading criteria with the new questions",
  "Checking the revised script still reads as one interview",
];

const SLOW_MESSAGE = "Still refining — thorough scripts can take up to two minutes";
const READY_MESSAGE = "Your script is ready";

export const getGenerationSteps = (mode: generateMode): string[] =>
  mode === generateMode.REGENERATE ? regenerateSteps : initialSteps;

// How long each step runs before the next starts. Front-loaded so the build
// moves quickly at first, then slows to fit a typical 30–60s generation. The
// final step has no entry: it waits on the request.
const STEP_DELAYS_MS = [3_000, 5_000, 8_000, 10_000, 12_000];

// Past this, reassure the user the request is still alive.
const SLOW_AFTER_MS = 45_000;

type Phase = "idle" | "running" | "done";

export const useGenerationProgress = (steps: string[]) => {
  const [phase, setPhase] = useState<Phase>("idle");
  const [stepIndex, setStepIndex] = useState(0);
  const [isSlow, setIsSlow] = useState(false);

  // Advances through the steps on their schedule, stopping at the last one.
  useEffect(() => {
    if (phase !== "running" || stepIndex >= steps.length - 1) return;
    const delay = STEP_DELAYS_MS[stepIndex] ?? STEP_DELAYS_MS[STEP_DELAYS_MS.length - 1];
    const timer = setTimeout(() => setStepIndex((index) => index + 1), delay);
    return () => clearTimeout(timer);
  }, [phase, stepIndex, steps.length]);

  useEffect(() => {
    if (phase !== "running") return;
    const timer = setTimeout(() => setIsSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const start = useCallback(() => {
    setStepIndex(0);
    setIsSlow(false);
    setPhase("running");
  }, []);

  // The request resolved: jump to the end, whatever step the timer reached.
  const finish = useCallback(() => {
    setStepIndex(steps.length - 1);
    setPhase("done");
  }, [steps.length]);

  const reset = useCallback(() => {
    setPhase("idle");
    setStepIndex(0);
    setIsSlow(false);
  }, []);

  const isRunningLastStep = phase === "running" && stepIndex === steps.length - 1;

  // The one line shown (and announced) under the outline. Steps read as
  // in-progress, so they get an ellipsis; the reassurance and the result are
  // full sentences and don't.
  let statusMessage = `${steps[stepIndex]}…`;
  if (phase === "done") statusMessage = READY_MESSAGE;
  else if (isSlow && isRunningLastStep) statusMessage = SLOW_MESSAGE;

  return {
    stepIndex,
    statusMessage,
    isActive: phase !== "idle",
    isDone: phase === "done",
    start,
    finish,
    reset,
  };
};
